import { pathOnly, referrerForEvent, sanitizeProperties } from "./sanitize";
import type { TelemetryEvent, TelemetryConfig } from "./types";

// `NEXT_PUBLIC_TELEMETRY_API_URL` is injected per stage by build-stage.ts; the
// literal is the local fallback and must stay last (tdd-0093).
const DEFAULT_ENDPOINT =
  process.env.NEXT_PUBLIC_TELEMETRY_API_URL || "https://api.telemetry.l.gaz.codes";
const DEFAULT_FLUSH_INTERVAL = 5000;
const DEFAULT_FLUSH_THRESHOLD = 20;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getSessionId(): string {
  if (typeof sessionStorage === "undefined") return "server";
  const key = "__telemetry_sid";
  let sid = sessionStorage.getItem(key);
  if (!sid) {
    sid = generateId();
    sessionStorage.setItem(key, sid);
  }
  return sid;
}

export class TelemetryClient {
  private queue: TelemetryEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private sessionId: string;
  private config: Required<
    Pick<TelemetryConfig, "endpoint" | "appName" | "enabled" | "debug" | "flushInterval" | "flushThreshold">
  >;

  constructor(config: TelemetryConfig) {
    this.config = {
      endpoint: config.endpoint ?? DEFAULT_ENDPOINT,
      appName: config.appName,
      enabled: config.enabled ?? true,
      debug: config.debug ?? false,
      flushInterval: config.flushInterval ?? DEFAULT_FLUSH_INTERVAL,
      flushThreshold: config.flushThreshold ?? DEFAULT_FLUSH_THRESHOLD,
    };
    this.sessionId = getSessionId();

    if (this.config.enabled && typeof window !== "undefined") {
      this.startFlushTimer();
      this.setupUnloadHandler();
    }
  }

  track(
    type: TelemetryEvent["type"],
    properties: Record<string, unknown> = {},
    overrides?: Partial<Pick<TelemetryEvent, "url" | "referrer">>
  ): void {
    if (!this.config.enabled) return;

    // Pathname only, never `location.href`: auth flows carry bearer tokens in
    // the query string (reset / magic-link / MFA links). See ./sanitize.ts.
    const rawUrl = overrides?.url ?? (typeof location !== "undefined" ? location.pathname : undefined);
    const rawReferrer =
      overrides?.referrer ?? (typeof document !== "undefined" ? document.referrer : undefined);

    const event: TelemetryEvent = {
      id: generateId(),
      type,
      timestamp: Date.now(),
      sessionId: this.sessionId,
      appName: this.config.appName,
      url: rawUrl ? pathOnly(rawUrl) : undefined,
      referrer: referrerForEvent(rawReferrer),
      properties: sanitizeProperties(properties),
    };

    if (this.config.debug) {
      console.log(`[telemetry] ${type}`, properties);
    }

    this.queue.push(event);

    if (this.queue.length >= this.config.flushThreshold) {
      this.flush();
    }
  }

  flush(): void {
    if (this.queue.length === 0) return;

    const events = this.queue.splice(0);
    const url = `${this.config.endpoint}/api/events/batch`;
    const body = JSON.stringify({ events });

    // Use sendBeacon if available (works during page unload)
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const sent = navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      if (sent) return;
    }

    // Fallback to fetch with keepalive
    try {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Silently ignore — telemetry must never break the app
    }
  }

  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => this.flush(), this.config.flushInterval);
  }

  private setupUnloadHandler(): void {
    const handler = () => this.flush();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") handler();
    });
    window.addEventListener("pagehide", handler);
  }
}
